@if (isInline) {
  <ng-container *ngTemplateOutlet="exchangeRatePickerFormTemplate" />
}

@if (isMenu) {
  <div
    class="w-full min-w-[2rem]"
    (click)="!disabled && exchangeRatePopover.toggle($event)"
  >
    <ng-container *ngTemplateOutlet="inputTemplate" />
  </div>

  <p-popover
    #exchangeRatePopover
    (onHide)="onMenuClose()"
    (onShow)="onMenuOpen()"
  >
    <ng-container *ngTemplateOutlet="exchangeRateOverlay" />
  </p-popover>
}

<ng-template #exchangeRatePickerFormTemplate>
  @if (exchangeRateform) {
    <form [formGroup]="exchangeRateform">
      <div class="flex flex-col gap-3">
        <!-- currencies -->
        <div class="grid grid-cols-[1fr_auto_1fr] gap-3">
          <div class="flex flex-col">
            <label class="whitespace-nowrap" for="fromCurrency">
              From currency
            </label>
            @if (disableFromCurrencyInput) {
              <span
                class="!cursor-not-allowed rounded border !border-slate-200 px-3 py-2 !text-slate-400 !ring-0 placeholder:!text-slate-400 focus-within:!border-slate-200"
              >
                {{ fromCurrency?.value?.label }}
              </span>
            }
            @if (!disableFromCurrencyInput) {
              <ng-select
                id="fromCurrency"
                formControlName="fromCurrency"
                [items]="currencies"
                [clearable]="false"
                [searchable]="true"
              />
            }
          </div>
          <div class="flex flex-col">
            <span class="shrink-0">&nbsp;</span>
            <div class="grid grow place-items-center">
              <p-button
                size="small"
                severity="contrast"
                variant="text"
                (click)="swapCurrencies()"
                [pTooltip]="swapDisabled ? '' : 'Swap currencies'"
                [disabled]="swapDisabled"
              >
                <ng-template #icon>
                  @if (swapDisabled) {
                    <svg-icon src="/assets/icons/general/arrow-right.svg" />
                  }
                  @if (!swapDisabled) {
                    <svg-icon
                      src="/assets/icons/general/arrow-right-left.svg"
                    />
                  }
                </ng-template>
              </p-button>
            </div>
          </div>
          <div class="flex flex-col">
            <label class="whitespace-nowrap" for="toCurrency"
              >To currency</label
            >
            @if (disableToCurrencyInput) {
              <span
                class="!cursor-not-allowed rounded border !border-slate-200 px-3 py-2 !text-slate-400 !ring-0 placeholder:!text-slate-400 focus-within:!border-slate-200"
              >
                {{ toCurrency?.value?.label }}
              </span>
            }
            @if (!disableToCurrencyInput) {
              <ng-select
                id="toCurrency"
                formControlName="toCurrency"
                [items]="currencies"
                [clearable]="false"
                [searchable]="true"
              />
            }
          </div>
        </div>
        <!-- exchange rate types -->
        <div>
          <label class="whitespace-nowrap" for="exchangeRateType">
            Select exchange rate type
          </label>
          <ng-select
            id="exchangeRateType"
            formControlName="exchangeRateType"
            [items]="exchangeRateTypes"
            [clearable]="false"
            [searchable]="true"
            [loading]="isLoading"
            bindLabel="title"
            bindValue="value"
            placeholder="Select type"
          >
            <ng-template ng-label-tmp let-item="item">
              {{ item.title }}
            </ng-template>
            <ng-template ng-option-tmp let-item="item">
              <div class="flex items-center gap-3 p-3">
                <svg-icon [src]="item.icon" [class]="item.color" />
                <div class="grid w-full grid-cols-1 gap-2">
                  <span class="truncate text-base font-semibold text-slate-800">
                    {{ item.title }}
                  </span>
                  <span class="truncate text-sm text-slate-500">
                    {{ item.subTitle }}
                  </span>
                </div>
              </div>
            </ng-template>
          </ng-select>
        </div>
        @if (isCustomRate) {
          <div>
            <label for="exchangeRate">Enter exchange rate</label>
            <ag1-financial-input
              id="exchangeRate"
              formControlName="exchangeRate"
              [maxDecimalPlaces]="maxDecimalPlaces"
            />
            @if (exchangeRate?.value) {
              <span class="text-sm text-slate-500">
                1 {{ fromCurrency?.value?.currencyCode }} =
                {{
                  $safeNavigationMigration(exchangeRate?.value)
                    | number: numberFormat
                }}
                {{ toCurrency?.value?.currencyCode }}
              </span>
            }
            @if (!exchangeRate?.value) {
              <span>&nbsp;</span>
            }
          </div>
        }
        @if (isSpotRate) {
          <div>
            <label for="exchangeRateDate">Select date</label>
            <input
              type="date"
              formControlName="exchangeRateDate"
              id="exchangeRateDate"
              [min]="dateConstantsService.MIN_DATE"
              [max]="dateConstantsService.MAX_DATE"
            />
          </div>
        }
        @if (isFixedRate || isSpotRate) {
          @if (errorMessage) {
            <span class="text-lg font-medium text-red-600">
              {{ errorMessage }}
            </span>
          }
          @if (!errorMessage) {
            <!-- current exchange rate -->
            @if (exchangeRate?.value && !isLoading) {
              <span class="text-lg font-medium text-french-blue-600">
                1 {{ fromCurrency?.value?.currencyCode }} =
                {{
                  $safeNavigationMigration(exchangeRate?.value)
                    | number: numberFormat
                }}
                {{ toCurrency?.value?.currencyCode }}
              </span>
            }
            @if (!exchangeRate?.value || isLoading) {
              <span class="text-lg font-medium text-french-blue-600">
                &nbsp;
              </span>
            }
          }
        }
      </div>
    </form>
  }
</ng-template>

<ng-template #inputTemplate>
  <div class="relative">
    <svg-icon
      svgClass="!w-4 !h-4"
      class="absolute left-3 top-1/2 -translate-y-1/2 text-french-blue-600"
      src="/assets/icons/general/cash.svg"
    />

    <div
      class="w-full rounded border border-slate-300 px-8 py-2"
      [class]="{
        '!cursor-not-allowed': disabled,
        '!border-slate-200': disabled,
        '!text-slate-400': disabled,
        '!ring-0': disabled,
        'placeholder:!text-slate-400': disabled,
        'focus-within:!border-slate-200': disabled,
        'ring-1': isActive,
        'ring-steel-blue-500': isActive,
        'ring-offset-0': isActive,
        'ring-offset-white': isActive,
      }"
      [attr.readonly]="true"
      [value]="displayValue"
    >
      <span class="select-none">
        @if (exchangeRate?.value) {
          1 {{ fromCurrency?.value?.currencyCode }} =
          {{ exchangeRate.value | number: numberFormat }}
          {{ toCurrency?.value?.currencyCode }}
        }
        @if (!exchangeRate?.value) {
          Set exchange rate
        }
      </span>
    </div>

    <svg-icon
      class="absolute right-3 top-1/2 -translate-y-1/2"
      [class]="disabled ? 'text-slate-400' : 'text-slate-800'"
      src="/assets/icons/general/chevron-down.svg"
    />
  </div>
</ng-template>

<ng-template #exchangeRateOverlay>
  <div class="w-96">
    <div class="flex flex-col gap-3">
      <ng-container *ngTemplateOutlet="exchangeRatePickerFormTemplate" />

      <div class="flex justify-end gap-2">
        <p-button
          size="small"
          label="Clear"
          severity="contrast"
          variant="text"
          (click)="onClear()"
        />
        <p-button
          size="small"
          label="Confirm"
          [disabled]="!exchangeRate?.value"
          (click)="onConfirm()"
        />
      </div>
    </div>
  </div>
</ng-template>
